/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Slider from 'react-slick';
import { Dropdown, SelectContainer, SelectWrapper } from './style';
import Text from '../Text';
import Icon from '../Icon';
import Checkbox from '../Checkbox';
import Badge from '../Badge';
import theme from '../../theme';
import { OutsideAlerter } from '../../../utils/useOutsideDetector';

const currentYearNum = Number(dayjs().format('YYYY'));
const YEARS_OPT = [];
for (let y = currentYearNum; y > currentYearNum - 5; y--) {
  YEARS_OPT.push({
    label: `${y}`,
    value: y,
  });
}
const YEARS_OPTS = [...YEARS_OPT];

const CustomArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: 'block' }}
      onClick={onClick}
    >
      <Icon type="chevron-left" />
    </div>
  );
};

CustomArrow.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

const baseSettings = {
  dots: false,
  infinite: true,
  slidesToShow: 3,
  slidesToScroll: 1,
  initialSlide: 0,
  rtl: true,
  centerMode: true,
  centerPadding: '0',
  arrows: true,
  nextArrow: <CustomArrow />,
  prevArrow: <CustomArrow />,
};

const TimeIntervalFilter = ({
  callback,
  singleMonth,
  size = 'm',
  forceSplitMonths = false,
  forcePosition = '',
  id = 'filter-time-interval',
  defaultStartDate,
  defaultEndDate,
  maxMonthsDiff = 99,
}) => {
  const selectRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentYear, setCurrentYear] = useState(dayjs().format('YYYY'));
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [tempEndDate, setTempEndDate] = useState();
  const [position, setPosition] = useState(forcePosition);
  const [splitMonths, setSplitMonths] = useState(forceSplitMonths);

  useEffect(() => {
    if (selectRef?.current) {
      if (
        !forcePosition &&
        selectRef?.current?.getBoundingClientRect()?.left >
          window.innerWidth / 2
      ) {
        setPosition('right');
      }

      if (
        !forceSplitMonths &&
        document.body.clientHeight -
          selectRef.current.getBoundingClientRect().bottom <
          600
      ) {
        setSplitMonths(true);
      }
    }
  }, [forcePosition, forceSplitMonths, selectRef, showDropdown]);

  const getMonths = (year) => {
    const months = [];
    for (let index = 0; index < 12; index++) {
      const month = dayjs(year).month(index);
      months.push({
        label: month.format('MMMM'),
        start_date: month.format('01-MM-YYYY'),
        start_date_label: month.format('01/MM/YY'),
        end_date: month.endOf('month').format('DD-MM-YYYY'),
        end_date_label: month.endOf('month').format('DD/MM/YY'),
        progressive: month.format('YYYYMM'),
        year: month.format('YYYY'),
      });
    }
    return months;
  };

  const getMonthsDiff = (month) => {
    if (startDate?.start_date) {
      const monthDiff = dayjs(month.start_date, 'DD-MM-YYYY').diff(
        dayjs(startDate.start_date, 'DD-MM-YYYY'),
        'months'
      );
      return monthDiff;
    }
    return 0;
  };

  const onClickMonth = (month) => {
    if (singleMonth) {
      setStartDate(month);
      setEndDate(month);
      return;
    }

    if (getMonthsDiff(month) > maxMonthsDiff - 1) {
      return;
    }

    if (!startDate || month?.progressive < startDate?.progressive) {
      setStartDate(month);
      setEndDate();
    } else if (month?.progressive === startDate?.progressive) {
      setStartDate();
      setEndDate();
    } else setEndDate(month);
  };

  const monthIsChecked = (month) => {
    return (
      (startDate?.progressive <= month?.progressive &&
        endDate?.progressive >= month?.progressive) ||
      startDate?.progressive === month?.progressive ||
      endDate?.progressive === month?.progressive
    );
  };

  const monthIsIncluded = (month) => {
    return (
      startDate?.progressive <= month?.progressive &&
      tempEndDate?.progressive >= month?.progressive &&
      getMonthsDiff(month) < maxMonthsDiff
    );
  };

  const onClose = () => {
    if (showDropdown) {
      if (endDate?.end_date) callback(startDate?.start_date, endDate?.end_date);
      else callback(startDate?.start_date, startDate?.end_date);
    }
    setShowDropdown(!showDropdown);
  };

  const capitalize = (str) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

  const getPlaceholder = (sd, ed) => {
    if (sd) {
      if (singleMonth) return `${capitalize(sd?.label)} ${sd?.year}`;
      if (ed) return `${sd.start_date_label} - ${ed.end_date_label}`;
      return `${sd.start_date_label} - ${sd.end_date_label}`;
    }
    return 'Analisi temporale';
  };

  return (
    <SelectWrapper id={id}>
      <SelectContainer
        onClick={onClose}
        size={size}
        ref={selectRef}
        tabIndex="0"
      >
        <Text type="h3" className="title ellipsis">
          {getPlaceholder(startDate, endDate)}
        </Text>
        <Icon type="calendar" size={size === 's' ? 16 : 24} />
      </SelectContainer>
      {showDropdown && (
        <OutsideAlerter onClick={onClose}>
          <Dropdown onClick={(e) => e.stopPropagation()} position={position}>
            <div className="years-wrapper">
              <Slider {...baseSettings}>
                {(YEARS_OPTS || []).map(({ label }) => (
                  <div key={label}>
                    <Badge
                      type="outline"
                      color={theme().colors.link}
                      active={currentYear === label}
                      onClick={() => setCurrentYear(label)}
                    >
                      {label}
                    </Badge>
                  </div>
                ))}
              </Slider>
            </div>
            <div className={splitMonths ? 'd-flex' : ''}>
              <div className="months-wrapper">
                {getMonths(currentYear)
                  .slice(0, 6)
                  .map((m, i) => (
                    <div
                      key={`month-${i}`}
                      onMouseEnter={() => setTempEndDate(m)}
                      onMouseLeave={() => setTempEndDate()}
                      className={`${
                        monthIsIncluded(m) &&
                        dayjs().format('YYYYMM') >= m.progressive
                          ? 'month active'
                          : 'month'
                      } ${
                        getMonthsDiff(m) < maxMonthsDiff || singleMonth
                          ? 'cursor-pointer'
                          : 'cursor-default'
                      }`}
                    >
                      <Checkbox
                        label={m?.label}
                        onChange={() => onClickMonth(m)}
                        checked={monthIsChecked(m)}
                        disabled={dayjs().format('YYYYMM') < m.progressive}
                      />
                    </div>
                  ))}
              </div>
              <div className="months-wrapper">
                {getMonths(currentYear)
                  .slice(6, 12)
                  .map((m, i) => (
                    <div
                      key={`month-${i}`}
                      onMouseEnter={() => setTempEndDate(m)}
                      onMouseLeave={() => setTempEndDate()}
                      className={`${
                        monthIsIncluded(m) &&
                        dayjs().format('YYYYMM') >= m.progressive
                          ? 'month active'
                          : 'month'
                      } ${
                        getMonthsDiff(m) < maxMonthsDiff || singleMonth
                          ? 'cursor-pointer'
                          : 'cursor-default'
                      }`}
                    >
                      <Checkbox
                        label={m?.label}
                        onChange={() => onClickMonth(m)}
                        checked={monthIsChecked(m)}
                        disabled={dayjs().format('YYYYMM') < m.progressive}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </Dropdown>
        </OutsideAlerter>
      )}
    </SelectWrapper>
  );
};

TimeIntervalFilter.propTypes = {
  size: PropTypes.oneOf(['s', 'm', 'full']),
  callback: PropTypes.func.isRequired,
  forceSplitMonths: PropTypes.bool,
  forcePosition: PropTypes.string,
  id: PropTypes.string,
  singleMonth: PropTypes.bool,
  defaultStartDate: PropTypes.object,
  defaultEndDate: PropTypes.object,
  maxMonthsDiff: PropTypes.number,
};

export default TimeIntervalFilter;
